import {
  APIComponentAttributes,
  ExternalDocumentProvider,
  PersistedExternalDocument,
} from '@ardoq/api-types';
import { getArdoqErrorTraceId } from '@ardoq/common-helpers';
import { I18nLocale, t } from '@ardoq/i18n';
import { confirm } from '@ardoq/modal';
import { CellValueChangedEvent, GridApi, IRowNode } from 'ag-grid-enterprise';
import {
  BulkEditEventSource,
  ColumnDefinitionArdoqMetadata,
  ExtendedRowNode,
  InventoryContext,
  PendingLucidchartChange,
} from '../types';
import { createComponentsOperations } from '../createComponents/createComponentsOperations';

export type LucidchartCellValueChangedEvent = CellValueChangedEvent<
  APIComponentAttributes,
  PersistedExternalDocument[],
  InventoryContext
>;

export const toPendingLucidchartChange = (
  event: LucidchartCellValueChangedEvent
): PendingLucidchartChange | null => {
  const componentId = event.data?._id;
  const columnContext = event.colDef.context as
    | ColumnDefinitionArdoqMetadata
    | undefined;
  if (!componentId || columnContext?.inventoryColumnType !== 'lucidchart') {
    return null;
  }

  const currentDocuments = (
    event.context.externalDocumentsByComponentId[componentId] ?? []
  ).filter(
    document => document.provider === ExternalDocumentProvider.LUCIDCHART
  );
  const pastedDocumentIds = (event.newValue ?? []).map(
    document => document.externalId
  );

  const documentIdsToLink = pastedDocumentIds.filter(
    documentId =>
      !currentDocuments.some(document => document.externalId === documentId)
  );
  const documentsToUnlink = currentDocuments.filter(
    document => !pastedDocumentIds.includes(document.externalId)
  );

  if (documentIdsToLink.length === 0 && documentsToUnlink.length === 0) {
    return null;
  }

  return { componentId, documentIdsToLink, documentsToUnlink };
};

const showEditLucidchartDocumentsFailedDialog = async (
  i18nLocale: I18nLocale,
  traceId: string | null
): Promise<void> => {
  await confirm({
    title: t(
      i18nLocale,
      'inventory:editLucidchartFailed.title',
      'Editing Lucidchart documents failed'
    ),
    text: t(
      i18nLocale,
      'inventory:editLucidchartFailed.text',
      'Your changes could not be saved and will be reverted.'
    ),
    text2:
      traceId &&
      t(
        i18nLocale,
        'inventory:editLucidchartFailed.traceId',
        { traceId },
        `Trace ID: ${traceId}`
      ),
    confirmButtonTitle: t(i18nLocale, 'inventory:dialogs.ok', 'OK'),
    cancelButtonTitle: '',
  });
};

const applyChanges = async (
  changes: PendingLucidchartChange[],
  context: InventoryContext
): Promise<boolean> => {
  const outcome = await context.commands.applyLucidchartChanges(changes);
  if (outcome.status === 'failed') {
    await showEditLucidchartDocumentsFailedDialog(
      context.i18nLocale,
      getArdoqErrorTraceId(outcome.error)
    );
    return false;
  }
  return true;
};

export const editLucidchartDocumentsSingleComponent = async (
  event: LucidchartCellValueChangedEvent
): Promise<boolean> => {
  const pendingChange = toPendingLucidchartChange(event);
  if (!pendingChange) {
    return true;
  }
  return applyChanges([pendingChange], event.context);
};

export const markNodeAsPendingLucidchartChange = (
  event: LucidchartCellValueChangedEvent
) => {
  const pendingChange = toPendingLucidchartChange(event);
  if (!pendingChange) {
    return;
  }
  const node = event.node as ExtendedRowNode;
  node._pendingLucidchartChanges = [
    ...(node._pendingLucidchartChanges ?? []),
    pendingChange,
  ];
};

export const collectPendingLucidchartChangesFromNodes = (
  api: GridApi<APIComponentAttributes>
): PendingLucidchartChange[] => {
  const pendingLucidchartChanges: PendingLucidchartChange[] = [];
  const collect = (node?: IRowNode<APIComponentAttributes> | null) => {
    const changes = (node as ExtendedRowNode | undefined)
      ?._pendingLucidchartChanges;
    if (changes) {
      pendingLucidchartChanges.push(...changes);
    }
  };

  api.forEachNode(collect);

  const pinnedTopRowCount = api.getPinnedTopRowCount();
  for (let i = 0; i < pinnedTopRowCount; i++) {
    const node = api.getPinnedTopRow(i);
    if (
      node?.data &&
      !createComponentsOperations.isComponentNotPersisted(node.data._id)
    ) {
      collect(node);
    }
  }

  return pendingLucidchartChanges;
};

export const clearPendingLucidchartChangesFromNodes = (
  api: GridApi<APIComponentAttributes>
) => {
  api.forEachNode(node => {
    (node as ExtendedRowNode)._pendingLucidchartChanges = undefined;
  });
  const pinnedTopRowCount = api.getPinnedTopRowCount();
  for (let i = 0; i < pinnedTopRowCount; i++) {
    const node = api.getPinnedTopRow(i);
    if (node) {
      (node as ExtendedRowNode)._pendingLucidchartChanges = undefined;
    }
  }
};

export const multiUpdateLucidchartDocuments = async (
  pendingChanges: PendingLucidchartChange[],
  context: InventoryContext,
  api: GridApi<APIComponentAttributes>,
  _eventSource: BulkEditEventSource
): Promise<boolean> => {
  if (pendingChanges.length === 0) {
    return false;
  }

  await applyChanges(pendingChanges, context);
  clearPendingLucidchartChangesFromNodes(api);
  return true;
};
